/**
 * Connections-side env-var override reader for feature flags.
 *
 * Each declared flag is bound to its `VITE_FEATURE_<UPPER_SNAKE>` env
 * var via a LITERAL `import.meta.env.VITE_FEATURE_*` reference. Vite's
 * `define` plugin statically replaces those literals with the baked
 * string (or `undefined`) at build time, guaranteeing the override is
 * inlined into the production bundle. Dynamic access patterns like
 * `import.meta.env[key]` rely on Vite also inlining the full env
 * object — which works today but is fragile (subject to tree-shaking,
 * `envPrefix` changes, or future Vite optimizations).
 *
 * Adding a new flag requires adding an entry to `OVERRIDE_READERS`
 * below; the `Record<FlagName, ...>` constraint surfaces missing
 * entries at compile time so the override can never be silently
 * broken when a flag is introduced.
 *
 * The override beats the rule. See `@benchmark/core/feature-flags`
 * (`evaluate.ts`) for the precedence.
 *
 * To flip a flag in staging or production, set the matching
 * `VITE_FEATURE_*` GitHub Actions variable and re-run the deploy
 * workflow — the value is baked into the next bundle.
 *
 * @agentic connections
 */

import {
  parseFlagOverride,
  type FlagName,
} from "@benchmark/core/feature-flags";

/**
 * Per-flag lazy env-var reader. The arrow functions wrap literal
 * `import.meta.env.VITE_FEATURE_*` accesses so Vite statically
 * replaces them at build time. Lazy (not eager) so tests can use
 * `vi.stubEnv` to flip values between cases — eager capture at module
 * load would freeze the value before tests could stub it.
 * @agentic connections
 */
const OVERRIDE_READERS: Record<FlagName, () => string | undefined> = {
  inactivityLogout: () => import.meta.env.VITE_FEATURE_INACTIVITY_LOGOUT,
  dialpadCareTimeAutoCreate: () =>
    import.meta.env.VITE_FEATURE_DIALPAD_CARE_TIME_AUTO_CREATE,
  dialpadCallSelector: () => import.meta.env.VITE_FEATURE_DIALPAD_CALL_SELECTOR,
  bandwidthDiagnostics: () =>
    import.meta.env.VITE_FEATURE_BANDWIDTH_DIAGNOSTICS,
  demoMode: () => import.meta.env.VITE_FEATURE_DEMO_MODE,
};

/**
 * Read the per-flag `VITE_FEATURE_*` env var (Vite static replacement)
 * and parse it into an override decision. Returns `undefined` when no
 * override is set, meaning "use the rule."
 * @agentic connections
 */
export const readFeatureOverride = (name: FlagName): boolean | undefined => {
  // `name` is a closed `FlagName` union — the dynamic Record property
  // access is safe.
  // eslint-disable-next-line security/detect-object-injection
  const read = OVERRIDE_READERS[name];
  return parseFlagOverride(read());
};
